// AiDiagramPipelinePreview — cost-preview + live pipeline-stepper för
// diagram-intent. Extraherad ur AiAssistantDialog (#1 i
// PRE_RELEASE_REVIEW_100).
//
// - Idle: visar estimate från planDiagramFromPrompt + all-pending steps.
// - Running/done: visar live state från useDiagramPipeline.

import { useState } from 'react';
import { Sparkles, Download, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AiAgentStepper, type AgentStepState } from '@/components/ai/AiAgentStepper';
import { estimatePipelineCost, estimateSkillCost } from '@/lib/aiSkills/costEstimator';
import { planDiagramFromPrompt } from '@/lib/aiSkills/diagramFromPrompt';
import { DIAGRAM_PIPELINE, getAgent } from '@/lib/aiAgents/agentRegistry';
import type { UseDiagramPipelineResult } from '@/hooks/useDiagramPipeline';
import type { MultiSheetWorkflowState, WorkflowState } from '@/types/workflow';

interface AiDiagramPipelinePreviewProps {
  input: string;
  isLoading: boolean;
  diagramPipeline: UseDiagramPipelineResult;
  onImport: (state: WorkflowState | MultiSheetWorkflowState, overLimit?: boolean) => void;
  onViewIn3D?: () => void;
  onOpenChange: (open: boolean) => void;
}

export function AiDiagramPipelinePreview({
  input,
  isLoading,
  diagramPipeline,
  onImport,
  onViewIn3D,
  onOpenChange,
}: AiDiagramPipelinePreviewProps) {
  // #28: lokal "imported"-state guardar mot dubbelklick mellan onImport
  // och att dialogen hinner stängas. Visar bekräftelse-tillstånd ~1 s så
  // användaren ser att klicket gick fram.
  const [imported, setImported] = useState(false);
  const isLive = diagramPipeline.status !== 'idle';
  const plan = planDiagramFromPrompt({ userPrompt: input });
  const pipelineCost = estimatePipelineCost(plan, DIAGRAM_PIPELINE.map((s) => s.agentId));
  const skillTotal = estimateSkillCost(plan).total;
  const previewSteps: AgentStepState[] = DIAGRAM_PIPELINE.map((step) => {
    const agent = getAgent(step.agentId);
    return {
      agentId: step.agentId,
      label: agent?.description.split('.')[0] ?? step.agentId,
      status: 'pending',
      cost: pipelineCost.byAgent?.[step.agentId],
    };
  });
  const steps = isLive
    ? diagramPipeline.steps.map((s, i) => ({
        ...s,
        cost: s.cost ?? previewSteps[i]?.cost,
      }))
    : previewSteps;
  // When the pipeline failed at preflight (e.g. insufficient
  // balance) no step actually ran, so `diagramPipeline.totalCost`
  // is still 0. Falling through to the estimate keeps the
  // displayed total consistent with the per-step costs shown
  // above ("Total kostnad: 0 credits" alongside 60+80+20+60+80
  // step labels was confusing).
  const runningTotal = isLive
    ? (diagramPipeline.totalCost > 0 ? diagramPipeline.totalCost : pipelineCost.total)
    : pipelineCost.total;
  const statusLabel =
    diagramPipeline.status === 'running' ? 'Pipeline pågår' :
    diagramPipeline.status === 'done' ? 'Pipeline klar' :
    diagramPipeline.status === 'failed' ? 'Pipeline misslyckades' :
    diagramPipeline.status === 'cancelled' ? 'Avbruten' :
    'Diagram-intent';

  return (
    <details className="text-xs" open={isLive} data-testid="ai-diagram-intent-preview">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground flex items-center gap-1.5 select-none">
        <Sparkles className="w-3 h-3" />
        <span>
          {statusLabel} — {isLive ? '' : '~'}
          <strong>{runningTotal} credits</strong>{' '}
          {!isLive && (
            <span className="text-[10px] opacity-70">
              ({skillTotal} tools + {pipelineCost.total - skillTotal} agents)
            </span>
          )}
        </span>
      </summary>
      <div className="mt-2 space-y-2">
        <AiAgentStepper steps={steps} totalCost={runningTotal} />
        <div className="flex gap-2">
          {diagramPipeline.status === 'idle' && (
            <Button
              size="sm"
              onClick={() => { void diagramPipeline.start(input); }}
              disabled={isLoading || input.trim().length === 0}
              data-testid="ai-diagram-run-pipeline"
              className="gap-1.5"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Run pipeline
            </Button>
          )}
          {diagramPipeline.status === 'running' && (
            <Button size="sm" variant="outline" onClick={diagramPipeline.cancel}>
              Avbryt
            </Button>
          )}
          {diagramPipeline.status === 'done' && diagramPipeline.builtWorkspace && (
            <Button
              size="sm"
              onClick={() => {
                if (imported) return;
                if (!diagramPipeline.builtWorkspace) return;
                setImported(true);
                onImport(diagramPipeline.builtWorkspace);
                diagramPipeline.reset();
                onOpenChange(false);
              }}
              disabled={imported}
              data-testid="ai-diagram-import-to-canvas"
              className="gap-1.5"
            >
              {imported ? <Check className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
              {imported ? 'Importerat' : 'Importera till canvas'}
            </Button>
          )}
          {/* AA3: snabb väg till 3D-vy för multi-sheet-diagram. */}
          {diagramPipeline.status === 'done'
            && diagramPipeline.builtWorkspace
            && diagramPipeline.builtWorkspace.sheets.length >= 2
            && onViewIn3D && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                if (imported) return;
                if (!diagramPipeline.builtWorkspace) return;
                setImported(true);
                onImport(diagramPipeline.builtWorkspace);
                onViewIn3D();
                diagramPipeline.reset();
                onOpenChange(false);
              }}
              disabled={imported}
              data-testid="ai-diagram-view-3d"
              className="gap-1.5"
              title="Importera och öppna i 3D-vyn"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Importera + 3D
            </Button>
          )}
          {(diagramPipeline.status === 'done' || diagramPipeline.status === 'failed' || diagramPipeline.status === 'cancelled') && (
            <Button size="sm" variant="outline" onClick={diagramPipeline.reset}>
              Återställ
            </Button>
          )}
        </div>
        {diagramPipeline.status === 'done' && diagramPipeline.builtWorkspace && (
          <div className="text-[11px] text-muted-foreground">
            Byggt: {diagramPipeline.builtWorkspace.sheets.length} sheets,{' '}
            {diagramPipeline.builtWorkspace.sheets.reduce((n, s) => n + s.blocks.length, 0)} blocks
          </div>
        )}
        {diagramPipeline.report?.firstFailure && (
          <div className="text-[11px] text-destructive">
            {diagramPipeline.report.firstFailure.agent}: {diagramPipeline.report.firstFailure.error}
          </div>
        )}
      </div>
    </details>
  );
}
